import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { detecterTypeAnalyse, formatDonneesResultat } from '@/lib/analysesSpeciales';

interface PatientRecherche {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string | null;
  sexe: string | null;
  numero_dossier: string | null;
  telephone: string | null;
}

interface ResultatHistorique {
  id: string;
  valeur: number | null;
  valeur_texte: string | null;
  donnees: Record<string, string> | null;
  fichier_url: string | null;
  analyse_nom: string;
  analyse_code: string;
  analyse_unite: string;
}

interface DossierHistorique {
  id: string;
  date_analyse: string;
  statut: string;
  conclusion: string | null;
  resultats: ResultatHistorique[];
}

const formatDate = (d: string | null) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatDateCourte = (d: string | null) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

const calculerAge = (dateNaissance: string | null) => {
  if (!dateNaissance) return null;
  const birth = new Date(dateNaissance);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const statutBadge = (s: string | null | undefined) => {
  switch (s) {
    case 'termine':
      return { text: 'Terminé', cls: 'bg-green-100 text-green-700 border-green-200' };
    case 'en_cours':
      return { text: 'En cours', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
    case 'brouillon':
      return { text: 'Brouillon', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
    default:
      return { text: s || '—', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
  }
};

export default function HistoriqueAnalyses() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [patients, setPatients] = useState<PatientRecherche[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [selectedPatient, setSelectedPatient] = useState<PatientRecherche | null>(null);
  const [dossiers, setDossiers] = useState<DossierHistorique[]>([]);
  const [loadingDossiers, setLoadingDossiers] = useState(false);
  const [dossiersError, setDossiersError] = useState('');

  const searchPatients = async (term: string) => {
    setSearch(term);
    setSearchError('');
    if (!term.trim()) {
      setPatients([]);
      return;
    }
    setSearching(true);
    try {
      const q = term.trim();
      const { data, error } = await supabase
        .from('patients')
        .select('id, nom, prenom, date_naissance, sexe, numero_dossier, telephone')
        .or(`nom.ilike.%${q}%,prenom.ilike.%${q}%,numero_dossier.ilike.%${q}%,telephone.ilike.%${q}%`)
        .order('nom', { ascending: true })
        .order('prenom', { ascending: true })
        .limit(50);

      if (error) throw error;
      setPatients((data || []) as PatientRecherche[]);
    } catch (err: any) {
      console.error('Erreur recherche patients:', err);
      setSearchError(err.message || 'Erreur lors de la recherche');
      setPatients([]);
    } finally {
      setSearching(false);
    }
  };

  const selectPatient = async (patient: PatientRecherche) => {
    setSelectedPatient(patient);
    setDossiers([]);
    setDossiersError('');
    setLoadingDossiers(true);

    try {
      const { data: dossiersData, error: dossiersErr } = await supabase
        .from('dossiers_analyses')
        .select('id, date_analyse, statut, conclusion')
        .eq('patient_id', patient.id)
        .order('date_analyse', { ascending: false });

      if (dossiersErr) throw dossiersErr;

      if (dossiersData && dossiersData.length > 0) {
        const dossierIds = dossiersData.map((d: any) => d.id);
        const { data: resultatsData, error: resultatsErr } = await supabase
          .from('resultats_analyses')
          .select(`
            id, dossier_id, valeur, valeur_texte, donnees, fichier_url,
            analyse:analyses_biochimie_catalogue(id, code, nom, unite)
          `)
          .in('dossier_id', dossierIds)
          .order('created_at', { ascending: true });

        if (resultatsErr) throw resultatsErr;

        const resultsByDossier = new Map<string, ResultatHistorique[]>();
        (resultatsData || []).forEach((r: any) => {
          const entry: ResultatHistorique = {
            id: r.id,
            valeur: r.valeur,
            valeur_texte: r.valeur_texte,
            donnees: r.donnees,
            fichier_url: r.fichier_url,
            analyse_nom: r.analyse?.nom || '',
            analyse_code: r.analyse?.code || '',
            analyse_unite: r.analyse?.unite || '',
          };
          if (!resultsByDossier.has(r.dossier_id)) resultsByDossier.set(r.dossier_id, []);
          resultsByDossier.get(r.dossier_id)!.push(entry);
        });

        setDossiers(
          dossiersData.map((d: any) => ({
            id: d.id,
            date_analyse: d.date_analyse,
            statut: d.statut,
            conclusion: d.conclusion,
            resultats: resultsByDossier.get(d.id) || [],
          })),
        );
      } else {
        setDossiers([]);
      }
    } catch (err: any) {
      console.error('Erreur chargement analyses patient:', err);
      setDossiersError(err.message || 'Erreur lors du chargement des analyses');
    } finally {
      setLoadingDossiers(false);
    }
  };

  const backToSearch = () => {
    setSelectedPatient(null);
    setDossiers([]);
    setDossiersError('');
  };

  const age = selectedPatient ? calculerAge(selectedPatient.date_naissance) : null;

  return (
    <div>
      {/* Barre de recherche patient */}
      {!selectedPatient && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => searchPatients(e.target.value)}
              placeholder="Rechercher un patient (nom, prénom, n° dossier, téléphone)..."
              className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
            />
          </div>

          {searching && (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              <span className="text-sm">Recherche en cours...</span>
            </div>
          )}

          {searchError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <i className="ri-error-warning-line text-red-500 text-xl"></i>
              <p className="text-red-700 text-sm flex-1">{searchError}</p>
            </div>
          )}

          {!searching && !searchError && search.trim() && patients.length === 0 && (
            <div className="py-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-user-search-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-sm text-gray-500">Aucun patient trouvé pour « {search} »</p>
            </div>
          )}

          {!searching && patients.length > 0 && (
            <div className="mt-4 divide-y divide-gray-100">
              {patients.map((p) => {
                const pAge = calculerAge(p.date_naissance);
                return (
                  <button
                    key={p.id}
                    onClick={() => selectPatient(p)}
                    className="w-full flex items-center gap-4 py-3.5 px-3 rounded-xl hover:bg-teal-50/60 transition-colors cursor-pointer text-left"
                  >
                    <div className="w-11 h-11 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-heart-line text-lg text-teal-600"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        {p.nom} {p.prenom}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {p.sexe === 'F' ? 'Féminin' : p.sexe === 'M' ? 'Masculin' : '—'}
                        {pAge !== null && ` · ${pAge} an${pAge > 1 ? 's' : ''}`}
                        {p.numero_dossier && ` · Dossier ${p.numero_dossier}`}
                      </p>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-300 text-xl flex-shrink-0"></i>
                  </button>
                );
              })}
            </div>
          )}

          {!searching && !search.trim() && (
            <div className="py-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-user-search-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-sm text-gray-500">Saisissez un nom pour rechercher un patient</p>
            </div>
          )}
        </div>
      )}

      {/* Historique du patient sélectionné */}
      {selectedPatient && (
        <div className="space-y-4">
          {/* En-tête patient */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6">
            <div className="flex items-center gap-3">
              <button
                onClick={backToSearch}
                className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-user-heart-line text-xl text-teal-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-bold text-gray-900">
                  {selectedPatient.nom} {selectedPatient.prenom}
                </p>
                <p className="text-xs text-gray-500">
                  {selectedPatient.sexe === 'F' ? 'Féminin' : selectedPatient.sexe === 'M' ? 'Masculin' : '—'}
                  {age !== null && ` · ${age} an${age > 1 ? 's' : ''}`}
                  {selectedPatient.numero_dossier && ` · Dossier ${selectedPatient.numero_dossier}`}
                  {selectedPatient.telephone && ` · ${selectedPatient.telephone}`}
                </p>
              </div>
            </div>
          </div>

          {loadingDossiers && (
            <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center">
              <i className="ri-loader-4-line text-3xl text-teal-500 animate-spin"></i>
              <p className="mt-3 text-gray-500 text-sm">Chargement des analyses...</p>
            </div>
          )}

          {dossiersError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <i className="ri-error-warning-line text-red-500 text-xl"></i>
              <p className="text-red-700 text-sm flex-1">{dossiersError}</p>
            </div>
          )}

          {!loadingDossiers && !dossiersError && dossiers.length === 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-microscope-line text-3xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucune analyse</h3>
              <p className="text-gray-500 text-sm">Ce patient n'a encore aucune analyse enregistrée.</p>
            </div>
          )}

          {!loadingDossiers && !dossiersError && dossiers.length > 0 && (
            <div className="space-y-4">
              {dossiers.map((d) => {
                const st = statutBadge(d.statut);
                return (
                  <div key={d.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    {/* En-tête dossier */}
                    <div className="flex items-center justify-between gap-3 p-4 md:p-5 border-b border-gray-100">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className="ri-calendar-line text-teal-600"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{formatDate(d.date_analyse)}</p>
                          <p className="text-xs text-gray-500">
                            {d.resultats.length} résultat{d.resultats.length > 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-[10px] px-2.5 py-1 rounded-full font-semibold border ${st.cls}`}>
                          {st.text}
                        </span>
                        <button
                          onClick={() => navigate(`/laborantin/rapport-analyses/${d.id}`)}
                          className="px-3.5 py-2 bg-teal-600 text-white rounded-full text-xs font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-file-text-line mr-1"></i>Voir le rapport
                        </button>
                      </div>
                    </div>

                    {/* Résultats */}
                    <div className="p-4 md:p-5">
                      {d.resultats.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">Aucun résultat saisi pour ce dossier.</p>
                      ) : (
                        <div className="space-y-2.5">
                          {d.resultats.map((r) => {
                            const typeAnalyse = detecterTypeAnalyse(r.analyse_code);
                            const lignesDonnees = formatDonneesResultat(typeAnalyse, r.donnees);
                            const estStructure = lignesDonnees.length > 0;
                            const valeurText = r.valeur_texte
                              ? r.valeur_texte
                              : r.valeur !== null
                                ? `${r.valeur} ${r.analyse_unite || ''}`.trim()
                                : r.fichier_url
                                  ? 'Fichier joint'
                                  : '—';

                            return (
                              <div key={r.id} className="rounded-xl border border-gray-100 bg-gray-50/50 p-3.5">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold text-gray-800">{r.analyse_nom || 'Analyse'}</p>
                                    <p className="text-[10px] text-gray-400">{r.analyse_code}</p>
                                  </div>
                                  {!estStructure && (
                                    <span className="text-sm font-bold text-teal-700 flex-shrink-0">
                                      {valeurText}
                                    </span>
                                  )}
                                </div>

                                {estStructure && lignesDonnees.length > 0 && (
                                  <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                                    {lignesDonnees.map((l, i) => (
                                      <div key={i} className="bg-white border border-gray-100 rounded-lg px-2.5 py-1.5">
                                        <div className="text-[10px] text-gray-500">{l.label}</div>
                                        <div className="text-sm font-semibold text-gray-800">{l.valeur || '—'}</div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {r.fichier_url && (
                                  <a
                                    href={r.fichier_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 mt-2 text-xs text-teal-600 hover:text-teal-700 font-medium"
                                  >
                                    <i className="ri-file-pdf-2-line"></i>
                                    Voir le fichier
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {d.conclusion && (
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <p className="text-xs font-semibold text-gray-500 mb-1">Conclusion</p>
                          <p className="text-sm text-gray-800 whitespace-pre-line">{d.conclusion}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}